import { INITIAL_PAGE_SIZE } from './constants'
import { getStepSize, buildInitialVisibleCount } from './utils'
import { markEntering, animateCollapseAndRender } from './animations'

interface PriceListElements {
  $tabButtons: HTMLButtonElement[]
  $items: HTMLElement[]
  $showMoreBtn: HTMLButtonElement | null
  $collapseBtn: HTMLButtonElement | null
  $counterVisible: HTMLElement | null
  $counterTotal: HTMLElement | null
  $offersList: HTMLElement | null
}

interface PriceListState {
  activeTab: string
  visibleCount: Record<string, number>
}

const queryElements = ($root: HTMLElement): PriceListElements => ({
  $tabButtons: Array.from($root.querySelectorAll<HTMLButtonElement>('[data-js="price-list-tab"]')),
  $items: Array.from($root.querySelectorAll<HTMLElement>('li[data-tab]')),
  $showMoreBtn: $root.querySelector<HTMLButtonElement>('[data-js="price-list-show-more"]'),
  $collapseBtn: $root.querySelector<HTMLButtonElement>('[data-js="price-list-collapse"]'),
  $counterVisible: $root.querySelector<HTMLElement>('[data-js="price-list-counter-visible-value"]'),
  $counterTotal: $root.querySelector<HTMLElement>('[data-js="price-list-counter-total-value"]'),
  $offersList: $root.querySelector<HTMLElement>('.esim-price-list__offers-list'),
})

const getTabItems = ($items: HTMLElement[], tab: string): HTMLElement[] =>
  $items.filter(($item) => $item.dataset.tab === tab)

export function initEsimPriceList($root: HTMLElement): void {
  const { $tabButtons, $items, $showMoreBtn, $collapseBtn, $counterVisible, $counterTotal, $offersList } =
    queryElements($root)

  const tabs = [...new Set($items.map(($item) => $item.dataset.tab ?? ''))]

  const state: PriceListState = {
    activeTab: 'popular',
    visibleCount: buildInitialVisibleCount(tabs),
  }

  const cancelAnimations = (): void => {
    if ($offersList) {
      $offersList.style.height = ''
      $offersList.style.overflow = ''
      $offersList.style.transition = ''
    }
    $items.forEach(($item) => {
      $item.classList.remove('is-entering', 'is-collapsing')
      $item.style.removeProperty('--stagger-index')
    })
  }

  const updateVisibility = ($tabItems: HTMLElement[], visible: number): void => {
    $items.forEach(($item) => $item.setAttribute('hidden', ''))
    $tabItems.slice(0, visible).forEach(($item) => $item.removeAttribute('hidden'))
  }

  const updateTabNav = (): void => {
    $tabButtons.forEach(($btn) => {
      $btn.classList.toggle('button--transparent', $btn.dataset.tab !== state.activeTab)
    })
  }

  const updateFooter = (visible: number, total: number): void => {
    if ($counterVisible) $counterVisible.textContent = String(visible)
    if ($counterTotal) $counterTotal.textContent = String(total)
    $showMoreBtn?.toggleAttribute('hidden', visible >= total)
    $collapseBtn?.toggleAttribute('hidden', visible <= INITIAL_PAGE_SIZE)
  }

  const render = (): void => {
    const { activeTab, visibleCount } = state
    const $tabItems = getTabItems($items, activeTab)
    const total = $tabItems.length
    const visible = Math.min(visibleCount[activeTab] ?? INITIAL_PAGE_SIZE, total)

    cancelAnimations()
    updateVisibility($tabItems, visible)
    updateTabNav()
    updateFooter(visible, total)
  }

  $tabButtons.forEach(($btn) => {
    $btn.addEventListener('click', () => {
      state.activeTab = $btn.dataset.tab ?? 'popular'
      render()
    })
  })

  $showMoreBtn?.addEventListener('click', () => {
    const $tabItems = getTabItems($items, state.activeTab)
    const prevCount = state.visibleCount[state.activeTab] ?? INITIAL_PAGE_SIZE
    const previousVisible = Math.min(prevCount, $tabItems.length)

    const step = getStepSize($tabItems.length)
    state.visibleCount[state.activeTab] = prevCount + step

    const nextVisible = Math.min(state.visibleCount[state.activeTab], $tabItems.length)
    const $newCards = $tabItems.slice(previousVisible, nextVisible)

    // render() first (reveals cards and clears previous animations), then markEntering()
    // — both run synchronously before the browser paints, so no flash
    render()
    markEntering($newCards)
  })

  $collapseBtn?.addEventListener('click', () => {
    const $tabItems = getTabItems($items, state.activeTab)
    const currentVisible = Math.min(state.visibleCount[state.activeTab] ?? INITIAL_PAGE_SIZE, $tabItems.length)
    const $cardsToHide = $tabItems.slice(INITIAL_PAGE_SIZE, currentVisible)

    $root.scrollIntoView({ block: 'start' })

    if (!$offersList) {
      state.visibleCount[state.activeTab] = INITIAL_PAGE_SIZE
      render()
      return
    }

    animateCollapseAndRender($offersList, $cardsToHide, () => {
      state.visibleCount[state.activeTab] = INITIAL_PAGE_SIZE
      render()
    })
  })

  render()
}
